/**
 * Chat dock component for Graph RAG — with SSE streaming and conversation persistence.
 *
 * Features:
 * - SSE streaming for real-time token display (falls back to sync on error)
 * - Auto-saves conversations after each exchange (via sync endpoint)
 * - Loads previous conversations for the current company
 * - Conversation list dropdown with recent history
 * - New conversation button
 * - Delete conversation action
 */

import { useState, useRef, useEffect, memo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useChat,
  useConversations,
  useConversation,
  useDeleteConversation,
  conversationKeys,
} from '../../hooks/useCompanies';
import { useChatStream } from '../../hooks/useChatStream';
import type { ChatMessage, Citation, ConversationSummary } from '../../types';

interface ChatDockProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  currentTab: string;
}

// Authority tier configuration
const TIER_CONFIG: Record<number, { label: string; color: string; bgColor: string; icon: string }> =
  {
    1: {
      label: 'Authoritative',
      color: 'text-green-700',
      bgColor: 'bg-green-50 border-green-200',
      icon: '1',
    },
    2: {
      label: 'High Quality',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50 border-blue-200',
      icon: '2',
    },
    3: {
      label: 'Moderate',
      color: 'text-yellow-700',
      bgColor: 'bg-yellow-50 border-yellow-200',
      icon: '3',
    },
    4: {
      label: 'Signal Only',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50 border-gray-200',
      icon: '4',
    },
  };

const DEFAULT_TIER = {
  label: 'Signal Only',
  color: 'text-gray-600',
  bgColor: 'bg-gray-50 border-gray-200',
  icon: '4',
};

function getTierBadgeColor(tier: number): string {
  const colors: Record<number, string> = {
    1: 'bg-green-100 text-green-700 ring-green-600/20',
    2: 'bg-blue-100 text-blue-700 ring-blue-600/20',
    3: 'bg-yellow-100 text-yellow-700 ring-yellow-600/20',
    4: 'bg-gray-100 text-gray-600 ring-gray-600/20',
  };
  return colors[tier] ?? colors[4] ?? '';
}

function CitationBadge({ citation }: { citation: Citation }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  const tierConfig = TIER_CONFIG[citation.source.tier] ?? DEFAULT_TIER;

  // Close popup when clicking outside
  useEffect(() => {
    if (!isExpanded) return;

    function handleClickOutside(event: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  return (
    <span className="relative inline-block" ref={popupRef}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-medium rounded-full ring-1 ring-inset transition-all ${getTierBadgeColor(citation.source.tier)} hover:scale-110`}
        title={`${citation.source.source_name} (${tierConfig.label})`}
        data-testid="citation"
      >
        {citation.index}
      </button>
      {isExpanded && (
        <div
          className={`absolute bottom-full left-0 mb-2 w-72 p-3 bg-white rounded-lg shadow-xl border z-50 ${tierConfig.bgColor}`}
        >
          {/* Header with source name and tier */}
          <div className="flex items-start justify-between gap-2">
            <div className="font-medium text-gray-900 text-sm">{citation.source.source_name}</div>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${getTierBadgeColor(citation.source.tier)}`}
            >
              {tierConfig.label}
            </span>
          </div>

          {/* Source snippet */}
          <div className="mt-2 p-2 bg-white/80 rounded border border-gray-200">
            <p className="text-xs text-gray-700 italic line-clamp-3">
              &quot;{citation.text_snippet}&quot;
            </p>
          </div>

          {/* Metadata row */}
          <div className="mt-2 flex items-center justify-between text-[10px]">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">
                Relevance:{' '}
                <span className="font-medium text-gray-700">
                  {Math.round(citation.relevance_score * 100)}%
                </span>
              </span>
            </div>
            {citation.source.url && (
              <a
                href={citation.source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-800 font-medium flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                View source
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            )}
          </div>
        </div>
      )}
    </span>
  );
}

/**
 * PERF: Memoized to prevent re-renders when new messages are added.
 * Chat messages are immutable once received.
 */
const MessageBubble = memo(function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';

  // Count citations by tier for summary
  const tierCounts = message.citations?.reduce(
    (acc, c) => {
      acc[c.source.tier] = (acc[c.source.tier] || 0) + 1;
      return acc;
    },
    {} as Record<number, number>
  );

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] rounded-lg px-4 py-2 ${
          isUser ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-900'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        {message.citations && message.citations.length > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-300/50">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium text-gray-600 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                {message.citations.length} Source{message.citations.length > 1 ? 's' : ''}
              </div>
              {tierCounts && tierCounts[1] && (
                <span className="text-[10px] text-green-600 font-medium">
                  {tierCounts[1]} authoritative
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {message.citations.map((citation) => (
                <CitationBadge key={citation.index} citation={citation} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * Streaming message bubble — shows content as it arrives token-by-token.
 * Also displays streaming citations once they arrive after generation.
 */
function StreamingBubble({
  content,
  citations,
  isStreaming,
}: {
  content: string;
  citations: Array<{
    index: number;
    source: { source_id: string; source_name: string; tier: number; url?: string };
    relevance: number;
  }>;
  isStreaming: boolean;
}) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-lg px-4 py-2 bg-gray-100 text-gray-900">
        <p className="text-sm whitespace-pre-wrap">
          {content}
          {isStreaming && (
            <span className="inline-block w-1.5 h-4 bg-gray-400 animate-pulse ml-0.5 align-text-bottom" />
          )}
        </p>
        {citations.length > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-300/50">
            <div className="text-xs font-medium text-gray-600 flex items-center gap-1 mb-2">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {citations.length} Source{citations.length > 1 ? 's' : ''}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {citations.map((c) => (
                <span
                  key={c.index}
                  className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-medium rounded-full ring-1 ring-inset ${getTierBadgeColor(c.source.tier)}`}
                  title={`${c.source.source_name}`}
                >
                  {c.index}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Format a date string for display in conversation list */
function formatConversationDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/** Conversation history dropdown */
function ConversationList({
  conversations,
  onSelect,
  onDelete,
  onNewConversation,
  activeConversationId,
}: {
  conversations: ConversationSummary[];
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onNewConversation: () => void;
  activeConversationId: string | null;
}) {
  return (
    <div className="border-b border-gray-200 bg-gray-50">
      <div className="px-3 py-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
            Conversations
          </span>
          <button
            onClick={onNewConversation}
            className="text-[10px] text-primary-600 hover:text-primary-800 font-medium flex items-center gap-0.5"
            data-testid="new-conversation-btn"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            New
          </button>
        </div>
        {conversations.length === 0 ? (
          <p className="text-[10px] text-gray-400 py-1">No previous conversations</p>
        ) : (
          <div className="space-y-0.5 max-h-32 overflow-y-auto">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                className={`group flex items-center justify-between rounded px-2 py-1 text-xs cursor-pointer transition-colors ${
                  conv.id === activeConversationId
                    ? 'bg-primary-100 text-primary-800'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
                onClick={() => onSelect(conv.id)}
                data-testid="conversation-item"
              >
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{conv.title || 'Untitled conversation'}</p>
                  <p className="text-[10px] text-gray-400">
                    {conv.total_messages} msg{conv.total_messages !== 1 ? 's' : ''} ·{' '}
                    {formatConversationDate(conv.updated_at)}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(conv.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 ml-1 p-0.5 text-gray-400 hover:text-red-500 transition-opacity"
                  title="Delete conversation"
                  data-testid="delete-conversation-btn"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ChatDock({ isOpen, onClose, companyId, companyName, currentTab }: ChatDockProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Streaming hook (primary)
  const stream = useChatStream();

  // Sync fallback hook
  const chatMutation = useChat();
  const deleteMutation = useDeleteConversation();

  // Fetch conversation list for this company
  const { data: conversationsData } = useConversations(companyId, isOpen);
  const conversations = conversationsData?.conversations ?? [];

  // Fetch selected conversation detail when loading a previous conversation
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null);
  const { data: loadedConversation } = useConversation(
    loadingConversationId,
    !!loadingConversationId
  );

  // When a conversation is loaded, populate the messages
  useEffect(() => {
    if (loadedConversation && loadingConversationId) {
      const chatMessages: ChatMessage[] = loadedConversation.messages.map(
        (m: {
          id: string;
          role: ChatMessage['role'];
          content: string;
          citations?: Citation[];
          timestamp: string;
        }) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          citations: m.citations?.length ? m.citations : undefined,
          timestamp: m.timestamp,
        })
      );
      setMessages(chatMessages);
      setConversationId(loadedConversation.id);
      setLoadingConversationId(null);
      setShowHistory(false);
    }
  }, [loadedConversation, loadingConversationId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Scroll when messages change or streaming content updates
  useEffect(() => {
    scrollToBottom();
  }, [messages, stream.content, scrollToBottom]);

  // Reset state when company changes
  useEffect(() => {
    setMessages([]);
    setConversationId(null);
    setShowHistory(false);
    setLoadingConversationId(null);
  }, [companyId]);

  // When streaming completes, finalize the message into the history
  useEffect(() => {
    if (stream.status === 'done' && stream.content) {
      const assistantMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: stream.content,
        citations: stream.citations.map((c) => ({
          index: c.index,
          source: c.source,
          relevance_score: c.relevance,
          text_snippet: '',
        })),
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      stream.reset();
    }
  }, [stream.status, stream.content, stream.citations, stream]);

  // When streaming errors, add error message
  useEffect(() => {
    if (stream.status === 'error') {
      const errorContent = stream.content
        ? `${stream.content}\n\n[Streaming interrupted: ${stream.error}]`
        : 'Sorry, I encountered an error. Please try again.';

      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: errorContent,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      stream.reset();
    }
  }, [stream.status, stream.content, stream.error, stream]);

  const isProcessing = stream.isStreaming || chatMutation.isPending;

  const handleNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setShowHistory(false);
  }, []);

  const handleSelectConversation = useCallback((id: string) => {
    setLoadingConversationId(id);
  }, []);

  const handleDeleteConversation = useCallback(
    async (id: string) => {
      try {
        await deleteMutation.mutateAsync(id);
        if (id === conversationId) {
          handleNewConversation();
        }
        queryClient.invalidateQueries({ queryKey: conversationKeys.list(companyId) });
      } catch {
        // Silently handle delete errors
      }
    },
    [conversationId, companyId, deleteMutation, handleNewConversation, queryClient]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputValue.trim() || isProcessing) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');

    const conversationHistory = messages.map((m) => ({
      role: m.role as 'user' | 'assistant',
      content: m.content,
    }));

    // Use streaming by default
    try {
      await stream.sendMessage({
        message: userMessage.content,
        context: {
          company_id: companyId,
          tab: currentTab,
          conversation_id: conversationId ?? undefined,
        },
        include_sources: true,
        conversation_history: conversationHistory,
      });
    } catch {
      // Streaming failed to even start — fall back to sync endpoint (which persists)
      try {
        const response = await chatMutation.mutateAsync({
          message: userMessage.content,
          context: {
            company_id: companyId,
            tab: currentTab,
            conversation_id: conversationId ?? undefined,
          },
          include_sources: true,
          conversation_history: conversationHistory,
        });

        const assistantMessage: ChatMessage = {
          id: response.id,
          role: 'assistant',
          content: response.message,
          citations: response.citations,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Track the conversation_id from the sync response (persistence)
        if (response.conversation_id) {
          setConversationId(response.conversation_id);
          queryClient.invalidateQueries({ queryKey: conversationKeys.list(companyId) });
        }
      } catch {
        const errorMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-16 bottom-0 w-80 bg-white border-l border-gray-200 flex flex-col z-30 shadow-lg">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-2">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Chat</h3>
            <p className="text-xs text-gray-500">Ask about {companyName}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`p-1 rounded transition-colors ${
              showHistory ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-500'
            }`}
            title={showHistory ? 'Hide history' : 'Show history'}
            data-testid="toggle-history-btn"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-500"
            title="Close chat"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Conversation history panel */}
      {showHistory && (
        <ConversationList
          conversations={conversations}
          onSelect={handleSelectConversation}
          onDelete={handleDeleteConversation}
          onNewConversation={handleNewConversation}
          activeConversationId={conversationId}
        />
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && !stream.isStreaming && (
          <div className="text-center text-gray-500 text-sm py-8">
            <p>Ask me anything about {companyName}.</p>
            <p className="mt-2 text-xs text-gray-400">
              I&apos;ll search the knowledge base and cite my sources.
            </p>
            <div className="mt-4 space-y-2">
              <button
                onClick={() => setInputValue(`What is ${companyName}'s revenue?`)}
                className="block w-full text-left text-xs bg-gray-50 hover:bg-gray-100 rounded px-3 py-2 text-gray-600"
              >
                &quot;What is {companyName}&apos;s revenue?&quot;
              </button>
              <button
                onClick={() => setInputValue(`Who are the executives at ${companyName}?`)}
                className="block w-full text-left text-xs bg-gray-50 hover:bg-gray-100 rounded px-3 py-2 text-gray-600"
              >
                &quot;Who are the executives?&quot;
              </button>
              <button
                onClick={() => setInputValue(`What products does ${companyName} offer?`)}
                className="block w-full text-left text-xs bg-gray-50 hover:bg-gray-100 rounded px-3 py-2 text-gray-600"
              >
                &quot;What products do they offer?&quot;
              </button>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}

        {/* Show streaming content as it arrives */}
        {(stream.isStreaming || (stream.status === 'connecting' && !stream.content)) &&
          stream.content && (
            <StreamingBubble
              content={stream.content}
              citations={stream.citations}
              isStreaming={stream.isStreaming}
            />
          )}

        {/* Typing indicator: show when connecting (before first token) */}
        {stream.status === 'connecting' && !stream.content && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '0ms' }}
                ></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '150ms' }}
                ></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '300ms' }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Sync fallback typing indicator */}
        {chatMutation.isPending && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '0ms' }}
                ></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '150ms' }}
                ></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '300ms' }}
                ></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Ask a question..."
            disabled={isProcessing}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isProcessing}
            className="rounded-lg bg-primary-600 px-3 py-2 text-white hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </div>
      </form>
    </div>
  );
}

export default ChatDock;
